import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

const base =
  'inline-flex items-center justify-center rounded-md font-semibold transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed';

const sizes = {
  small: 'h-9 px-3 text-sm',
  medium: 'h-11 px-4 text-base',
  large: 'h-14 px-6 text-lg'
};

const variants = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow hover:shadow-md',
  secondary:
    'bg-neutral-100 text-neutral-900 hover:bg-neutral-200 active:bg-neutral-300 border border-neutral-300',
  outline: 'bg-white text-brand-700 border border-brand-600 hover:bg-brand-50 active:bg-brand-100',
  ghost: 'bg-transparent text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200'
};

export default function Button({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  onClick,
  className = '',
  leftIcon,
  rightIcon,
  uppercase = false,
  bold = false,
  as,
  href,
  to,
  ...rest
}) {
  const classes = [
    base,
    sizes[size] || sizes.medium,
    variants[variant] || variants.primary,
    uppercase ? 'uppercase tracking-wide' : '',
    bold ? 'font-bold' : '',
    className
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {leftIcon && <span className="mr-2">{leftIcon}</span>}
      <span>{children}</span>
      {rightIcon && <span className="ml-2">{rightIcon}</span>}
    </>
  );

  if (as === 'a' && href) {
    return (
      <a href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      {...rest}
    >
      {content}
    </button>
  );
}

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'ghost']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  uppercase: PropTypes.bool,
  bold: PropTypes.bool
};
